/**
 * A contact's id on a survey, derived from the survey and the contact itself (#4558).
 *
 * `survey_addContact` skips a contact whose id it already holds, so a Save retried after the
 * first attempt landed (but its answer was lost) is recognised instead of adding a second
 * copy. The same contact on the same survey always gets the same id, ignoring case and
 * surrounding or repeated spaces; two identical contacts on one survey are one contact.
 *
 * Every app passes the SAME three fields, in this order: [full name, email, phone]. That is the
 * identity the server compares on a replay, so the same person gets the same id from every app
 * and editing notes or role does not turn a retry into a second contact. Changing the name,
 * email or phone is a different contact as far as a retry can tell.
 *
 * NEVER change the normalisation, the field order or the `c1-` prefix in place: an id already stored
 * on a survey would stop matching its own retry. A new scheme gets a new prefix (`c2-`) and the
 * server keeps recognising `c1-` ids.
 */

import { newClientId } from '../ids/newClientId';

/** cyrb53: a small, fast, non-cryptographic 53-bit string hash (identity, not security). */
function hash53(str: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/**
 * `fields` = [full name, email, phone]; a missing field is ''.
 * `clientId` is the per-add id the editor minted. It is folded in ONLY when there is no email and
 * no phone, because then the name alone cannot tell two different people apart (two "Mike"s on one
 * survey must both be stored). A retry of the same add carries the same clientId, so it is still
 * recognised. A contact without a clientId (queued before this existed) keeps the name-only id.
 */
export function stableContactId(
  surveyId: string,
  fields: ReadonlyArray<string | undefined>,
  clientId?: string,
): string {
  const noDetails = !(fields[1] ?? '').trim() && !(fields[2] ?? '').trim();
  const nonce = noDetails && clientId ? `\u0003${clientId}` : '';
  const key = fields.map((v) => (v ?? '').replace(/\s+/g, ' ').trim().toLowerCase()).join('\u0001');
  return `c1-${hash53(`${surveyId}\u0002${key}${nonce}`)}`;
}

/** A fresh per-add id for the contact editor (see newClientId for the fallback chain). */
export function newContactClientId(): string {
  return newClientId();
}
